import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AppWorkspaceNav from "./components/AppWorkspaceNav";
import AppWorkspaceMobileDock from "./components/AppWorkspaceMobileDock";
import AppWorkspaceTopStrip from "./components/AppWorkspaceTopStrip";
import CommandPalette from "./components/CommandPalette";
import AppProtectedRoute from "./components/AppProtectedRoute";
import AppLogin from "./pages/Login";
import AppSignup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import PostAuthSplash from "./pages/PostAuthSplash";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BondHouse from "./pages/BondHouse";
import BetDetail from "./pages/BetDetail";
import PlaceBetDesk from "./pages/PlaceBetDesk";
import P2P from "./pages/P2P";
import P2PCreate from "./pages/P2PCreate";
import P2PCreateMatch from "./pages/P2PCreateMatch";
import BondChat from "./pages/BondChat";
import Activity from "./pages/Activity";
import Predictions from "./pages/Predictions";
import CreatePrediction from "./pages/CreatePrediction";
import Dashboard from "./pages/Dashboard";
import BetMarketsHub from "./pages/BetMarketsHub";
import CreateBetHouseModal from "./components/bondhouse/CreateBetHouseModal";
import { HOME_PATH, isPausedAppPath, isProductEnabled } from "./config/products";
import OperatorConsole from "./pages/OperatorConsole";
import BettorsDesk from "./pages/BettorsDesk";
import TicketsDesk from "./pages/TicketsDesk";
import ViewersDesk from "./pages/ViewersDesk";
import FollowersDesk from "./pages/FollowersDesk";
import FixturesDesk from "./pages/FixturesDesk";
import AddFixturesDesk from "./pages/AddFixturesDesk";
import OddsHistoryDesk from "./pages/OddsHistoryDesk";
import CustomizeOddsDesk from "./pages/CustomizeOddsDesk";
import JoinedBets from "./pages/JoinedBets";
import Requests from "./pages/Requests";
import Referrals from "./pages/Referrals";
import Profile from "./pages/Profile";
import ProfileConnections from "./pages/ProfileConnections";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Community from "./pages/Community";
import BondPredict from "./pages/BondPredict";
import BondPredictDetail from "./pages/BondPredictDetail";
import BondPredictPortfolio from "./pages/BondPredictPortfolio";
import BondPlay from "./pages/BondPlay";
import BondCoin from "./pages/BondCoin";
import BondDice from "./pages/BondDice";
import BondNumbers from "./pages/BondNumbers";
import Leaderboard from "./pages/Leaderboard";
import BondWheel from "./pages/BondWheel";
import BondWheelPlayground from "./pages/BondWheelPlayground";
import { THEME } from "./theme";
import { checkAppAuth, selectAppIsAuthenticated, selectAppUser } from "./redux/appAuthSlice";
import { pingSessionHeartbeat } from "./redux/sessionCoolingSlice";
import SessionCoolingOverlay from "./components/SessionCoolingOverlay";
import SessionCoolingWarningBanner from "./components/SessionCoolingWarningBanner";
import NetworkStatusBanner from "../components/NetworkStatusBanner";
import { wireAxiosSessionCoolingListeners } from "./setup/wireAxiosSessionCooling";
import { store } from "../redux/store";
import { syncBondChatForUser } from "./redux/bondchat/generalChatSlice";
import { initializeActivitySocketListeners } from "./redux/activitySocket";
import { initWebPushNotifications } from "./setup/initWebPushNotifications";
import { CREATE_BET_HOUSE_QUERY, LEGACY_CREATE_BET_HOUSE_QUERY } from "./utils/createBetHouseHelpers";
import "./app.css";
import "./bondchat.css";

const Placeholder = ({ title }) => (
  <div className="app-page-shell mx-auto max-w-[1440px] px-4 py-10 sm:py-14">
    <div className="app-surface-card rounded-2xl p-8 sm:p-10">
      <div
        className="text-[11px] font-bold uppercase tracking-[0.22em]"
        style={{ color: THEME.text.tertiary }}
      >
        Section
      </div>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-white">
        {title}
      </h1>
      <p className="mt-3 max-w-lg text-[15px] leading-relaxed" style={{ color: THEME.text.secondary }}>
        This screen is next in the build. Navigation and auth are in place. Content comes here.
      </p>
    </div>
  </div>
);

const AppLayout = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const createHouseOpen =
    isProductEnabled("house") &&
    (new URLSearchParams(search).get(CREATE_BET_HOUSE_QUERY) === "1" ||
      new URLSearchParams(search).get(LEGACY_CREATE_BET_HOUSE_QUERY) === "1");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useEffect(() => {
    if (isPausedAppPath(pathname)) {
      navigate(HOME_PATH, { replace: true });
    }
  }, [pathname, navigate]);

  useEffect(() => {
    if (!createHouseOpen || isAuthenticated) return;
    navigate("/app/login", {
      replace: true,
      state: { from: `${pathname}${search}` },
    });
  }, [createHouseOpen, isAuthenticated, navigate, pathname, search]);

  const closeCreateHouse = useCallback(() => {
    const next = new URLSearchParams(search);
    next.delete(CREATE_BET_HOUSE_QUERY);
    next.delete(LEGACY_CREATE_BET_HOUSE_QUERY);
    const qs = next.toString();
    navigate({ pathname, search: qs ? `?${qs}` : "" }, { replace: true });
  }, [navigate, pathname, search]);

  const handleCreateHouseSuccess = useCallback(
    (houseId) => {
      closeCreateHouse();
      navigate(`/app/bethouse/manage/${houseId}`);
    },
    [closeCreateHouse, navigate]
  );
  const isBondChat = pathname.startsWith("/app/general-chat");
  const isPlaceBet = /^\/app\/bethouse\/[^/]+\/place$/.test(pathname);
  const isBetDetail =
    /^\/app\/bethouse\/[^/]+$/.test(pathname) &&
    pathname !== "/app/bethouse/manage";
  const isP2PCreate = pathname.startsWith("/app/p2p/create");
  const isPredCreate = pathname.startsWith("/app/predictions/create");
  const isActivity = pathname.startsWith("/app/activity");
  const isDashboard = pathname.startsWith("/app/dashboard");
  const isJoinedBets = pathname.startsWith("/app/joined-bets");
  const isRequests = pathname.startsWith("/app/requests");
  const isReferrals = pathname.startsWith("/app/referrals");
  const isProfile = pathname.startsWith("/app/profile");
  const isPolicyDesk =
    pathname.startsWith("/app/privacy") ||
    pathname.startsWith("/app/terms") ||
    pathname.startsWith("/app/community");
  const isOperatorConsole =
    pathname.startsWith("/app/bethouse/manage") ||
    /^\/app\/bethouse\/[^/]+\/manage/.test(pathname);
  const isBondPredict = pathname.startsWith("/app/bond-predict");
  const isBondPredictDetail =
    /^\/app\/bond-predict\/[^/]+$/.test(pathname) &&
    pathname !== "/app/bond-predict/portfolio";
  const isBondPlay = pathname.startsWith("/app/bond-play");
  const isBondCoin = pathname.startsWith("/app/bond-coin");
  const isBondDice = pathname.startsWith("/app/bond-dice");
  const isBondNumbers = pathname.startsWith("/app/bond-numbers");
  const isTipsFeed = pathname === "/app/predictions";
  const isLeaderboard = pathname.startsWith("/app/leaderboard");
  const hideNav =
    isBondChat ||
    isBetDetail ||
    isPlaceBet ||
    isDashboard ||
    isJoinedBets ||
    isBondPredict;
  const hideTopStrip =
    hideNav ||
    isActivity ||
    isLeaderboard ||
    isBondPredict ||
    isOperatorConsole ||
    isP2PCreate ||
    isPredCreate ||
    isRequests ||
    isReferrals;

  useEffect(() => {
    if (user?._id) {
      dispatch(syncBondChatForUser(user._id));
      initializeActivitySocketListeners(dispatch);
    }
  }, [user?._id, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return initWebPushNotifications({ navigate });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    document.documentElement.classList.add("app-scroll-plane");
    document.body.classList.add("app-scroll-plane");
    document.body.classList.add("app-has-workspace");
    document.body.classList.remove("app-has-deck");
    return () => {
      document.documentElement.classList.remove("app-scroll-plane");
      document.body.classList.remove("app-scroll-plane");
      document.body.classList.remove("app-has-workspace");
      document.body.classList.remove("app-hide-workspace-nav");
      document.body.classList.remove("app-console-fullbleed");
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("app-hide-workspace-nav", hideNav);
    document.body.classList.toggle("app-tips-feed", isTipsFeed);
    document.body.classList.toggle("app-leaderboard", isLeaderboard);
    document.body.classList.toggle("app-activity", isActivity);
    document.body.classList.toggle("app-bond-predict", isBondPredict);
    return () => {
      document.body.classList.remove("app-hide-workspace-nav");
      document.body.classList.remove("app-tips-feed");
      document.body.classList.remove("app-leaderboard");
      document.body.classList.remove("app-activity");
      document.body.classList.remove("app-bond-predict");
    };
  }, [hideNav, isTipsFeed, isLeaderboard, isActivity, isBondPredict]);

  // Operator console ships its own rail — drop the workspace sidebar entirely
  useEffect(() => {
    document.body.classList.toggle("app-console-fullbleed", isOperatorConsole);
    return () => document.body.classList.remove("app-console-fullbleed");
  }, [isOperatorConsole]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") setPaletteOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const mainClass = isBondChat
    ? "bc-main-shell"
    : isBetDetail
    ? "bd-main-shell"
    : isPlaceBet
    ? "pbd-main-shell"
    : isP2PCreate
    ? "p2pc-main-shell"
    : isPredCreate
    ? "prdc-main-shell"
    : isActivity
    ? "act-main-shell"
    : isDashboard
    ? "dash-main-shell"
    : isJoinedBets
    ? "jb-main-shell"
    : isRequests
    ? "req-main-shell"
    : isReferrals
    ? "ref-main-shell"
    : isProfile
    ? "prof-main-shell"
    : isPolicyDesk
    ? "pp-main-shell"
    : isOperatorConsole
    ? "oc-main-shell"
    : isBondPredict
    ? isBondPredictDetail
      ? "bp-detail-main-shell"
      : "bp-main-shell"
    : isBondPlay
    ? "bw-main-shell"
    : isBondCoin
    ? "bw-main-shell"
    : isBondDice
    ? "bw-main-shell"
    : isBondNumbers
    ? "bw-main-shell"
    : isLeaderboard
    ? "lb-main-shell"
    : "app-body";

  return (
    <div
      className={`app-workspace-shell${
        isOperatorConsole ? " app-workspace-shell-console" : ""
      }`}
    >
      {!isOperatorConsole && !isBondPredict && (
        <AppWorkspaceNav onOpenPalette={openPalette} />
      )}
      {!hideNav && <AppWorkspaceMobileDock onOpenPalette={openPalette} />}
      <div className="app-workspace-canvas">
        {!hideTopStrip && <AppWorkspaceTopStrip onOpenPalette={openPalette} />}
        <main className={mainClass}>
        <Routes>
          <Route index element={<Navigate to="welcome" replace />} />
          <Route path="p2p" element={<P2P />} />
          <Route
            path="p2p/create"
            element={
              <AppProtectedRoute>
                <P2PCreate />
              </AppProtectedRoute>
            }
          />
          <Route
            path="p2p/create/match"
            element={
              <AppProtectedRoute>
                <P2PCreateMatch />
              </AppProtectedRoute>
            }
          />
          <Route path="bethouse" element={<BondHouse />} />
          <Route
            path="bethouse/manage"
            element={
              <AppProtectedRoute>
                <BetMarketsHub />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/viewers"
            element={
              <AppProtectedRoute>
                <ViewersDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/followers"
            element={
              <AppProtectedRoute>
                <FollowersDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/fixtures"
            element={
              <AppProtectedRoute>
                <FixturesDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/fixtures/add"
            element={
              <AppProtectedRoute>
                <AddFixturesDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/fixtures/:fixtureId/odds"
            element={
              <AppProtectedRoute>
                <CustomizeOddsDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/fixtures/:fixtureId/odds-history"
            element={
              <AppProtectedRoute>
                <OddsHistoryDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/tickets"
            element={
              <AppProtectedRoute>
                <TicketsDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId/bettors"
            element={
              <AppProtectedRoute>
                <BettorsDesk />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/manage/:houseId"
            element={
              <AppProtectedRoute>
                <OperatorConsole />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/create"
            element={<Navigate to={HOME_PATH} replace />}
          />
          <Route
            path="bethouse/:houseId/manage"
            element={
              <AppProtectedRoute>
                <OperatorConsole />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bethouse/:houseId/place"
            element={
              <AppProtectedRoute>
                <PlaceBetDesk />
              </AppProtectedRoute>
            }
          />
          <Route path="bethouse/:houseId" element={<BetDetail />} />
          <Route
            path="bond-predict/portfolio"
            element={
              <AppProtectedRoute>
                <BondPredictPortfolio />
              </AppProtectedRoute>
            }
          />
          <Route path="bond-predict/:marketId" element={<BondPredictDetail />} />
          <Route path="bond-predict" element={<BondPredict />} />
          <Route path="bond-play" element={<BondPlay />} />
          <Route path="bond-coin" element={<BondCoin />} />
          <Route path="bond-dice" element={<BondDice />} />
          <Route path="bond-numbers" element={<BondNumbers />} />
          <Route
            path="bond-play/bond-wheel"
            element={
              <AppProtectedRoute>
                <BondWheel />
              </AppProtectedRoute>
            }
          />
          <Route
            path="bond-play/bond-wheel/:gameId"
            element={
              <AppProtectedRoute>
                <BondWheelPlayground />
              </AppProtectedRoute>
            }
          />
          <Route path="predictions" element={<Predictions />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route
            path="predictions/create"
            element={
              <AppProtectedRoute>
                <CreatePrediction />
              </AppProtectedRoute>
            }
          />
          <Route path="general-chat" element={<BondChat />} />
          <Route path="activity" element={<Activity />} />
          <Route
            path="joined-bets"
            element={
              <AppProtectedRoute>
                <JoinedBets />
              </AppProtectedRoute>
            }
          />
          <Route
            path="joined-bets/:ticketId"
            element={
              <AppProtectedRoute>
                <JoinedBets />
              </AppProtectedRoute>
            }
          />
          <Route
            path="requests"
            element={
              <AppProtectedRoute>
                <Requests />
              </AppProtectedRoute>
            }
          />
          <Route
            path="requests/:itemId"
            element={
              <AppProtectedRoute>
                <Requests />
              </AppProtectedRoute>
            }
          />
          <Route
            path="referrals"
            element={
              <AppProtectedRoute>
                <Referrals />
              </AppProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <AppProtectedRoute>
                <Dashboard />
              </AppProtectedRoute>
            }
          />
          <Route
            path="wallet"
            element={
              <AppProtectedRoute>
                <Placeholder title="Wallet" />
              </AppProtectedRoute>
            }
          />
          <Route
            path="profile/connections"
            element={
              <AppProtectedRoute>
                <Navigate to="followers" replace />
              </AppProtectedRoute>
            }
          />
          <Route
            path="profile/connections/:listType"
            element={
              <AppProtectedRoute>
                <ProfileConnections />
              </AppProtectedRoute>
            }
          />
          <Route
            path="profile"
            element={
              <AppProtectedRoute>
                <Profile />
              </AppProtectedRoute>
            }
          />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="community" element={<Community />} />
          <Route
            path="settings"
            element={
              <AppProtectedRoute>
                <Placeholder title="Settings" />
              </AppProtectedRoute>
            }
          />
          <Route path="*" element={<Placeholder title={`Not found: ${pathname}`} />} />
        </Routes>
      </main>
      </div>
      <CreateBetHouseModal
        open={createHouseOpen && isAuthenticated}
        onClose={closeCreateHouse}
        onSuccess={handleCreateHouseSuccess}
      />
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
};

const SessionCoolingBootstrap = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const coolingActive = useSelector((s) => s.sessionCooling.active);
  const consecutiveLimitMs = useSelector((s) => s.sessionCooling.consecutiveLimitMs);

  useEffect(() => {
    wireAxiosSessionCoolingListeners(store);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const ping = () => dispatch(pingSessionHeartbeat());
    ping();
    const heartbeatMs =
      consecutiveLimitMs && consecutiveLimitMs <= 5 * 60 * 1000 ? 15 * 1000 : 60 * 1000;
    const interval = window.setInterval(ping, heartbeatMs);
    const onVisibility = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, isAuthenticated, coolingActive, consecutiveLimitMs]);

  return (
    <>
      <NetworkStatusBanner />
      <SessionCoolingWarningBanner />
      <SessionCoolingOverlay />
    </>
  );
};

const AppShell = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkAppAuth());
  }, [dispatch]);

  return (
    <div className="app-page-shell min-h-screen">
      <SessionCoolingBootstrap />
      <Routes>
        <Route path="login" element={<AppLogin />} />
        <Route path="signup" element={<AppSignup />} />
        <Route path="verify" element={<VerifyEmail />} />
        <Route path="welcome" element={<PostAuthSplash />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="*" element={<AppLayout />} />
      </Routes>
    </div>
  );
};

export default AppShell;
